import React, { useEffect, useRef, useState } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import MediaManager from "../AdvisorProfilePage/MediaManager";
import LogoPicker from "./LogoPicker";
import { updateAssociation } from "../../services/associationService";
import {
  LogoSlot,
  buildAssociationImagesUpdate,
} from "../../services/associationMediaPayload";
import {
  PendingMediaFile,
  StoredMediaFile,
} from "../../services/mediaPayload";
import {
  AssociationRequest,
  AssociationResponse,
} from "../../types/association.types";
import {
  isAssociationNotAdministered,
  shouldRefetchAssociationsAfterError,
} from "../../utils/associationErrors";
import styles from "./MediaCard.module.scss";

// Association portal, slice 4: the media card. It hosts the logo slot over
// the association's `logo` and the shared MediaManager over its `images`
// (banners only), and saves both with its own button through
// `PUT /association/{id}` with `updateImages: true`. That rebuild replaces
// every stored row, the logo included, so the row set always carries the logo
// row (`isSmall: 1`, first) followed by the gallery rows built exactly like
// the advisor profile update (kept rows by stored location, new rows
// `location: null`, files appended in the order of the new rows). The scalars
// sent are the SERVER's current values from the context, never a half-typed
// edit from the data card, so a media save cannot smuggle one in.
//
// Seeding: the editor state is seeded from `association.logo` and
// `association.images` on mount, on a change of WHICH association is shown
// (`association.id`), and from the PUT response after this card's own
// successful save. A background re-read of `/mine` (another card's save, the
// context refresh) never touches it, so picked files and removals survive it.
//
// The page's cross-card lock: the card reports its save in flight
// (`onPendingChange`) and unsaved edits, i.e. picked files, removed stored
// rows or a changed logo (`onDirtyChange`, for the confirm before an
// association switch), both false on unmount, and holds its own save while
// `locked` (another card saves). The editor itself stays usable while locked.

export const MEDIA_SAVED_NOTICE = "Mediji su sačuvani.";
export const MEDIA_INVALID_INPUT_ERROR =
  "Podaci nisu prihvaćeni. Proverite unos i pokušajte ponovo.";
export const MEDIA_SAVE_ERROR = "Čuvanje nije uspelo. Pokušajte ponovo.";

type RejectionLike = {
  response?: {
    status?: number;
    data?: { errorCode?: string; message?: string };
  };
};

/**
 * 400 `ASSOCIATION_INVALID_INPUT` (row/file mismatch, missing name) shows the
 * backend message when there is one; `ASSOCIATION_MEDIA_UPLOAD_FAILED` and
 * everything else say nothing about the input and get the generic copy.
 */
export const mapMediaSaveError = (error: unknown): string => {
  const response =
    typeof error === "object" && error !== null
      ? (error as RejectionLike).response
      : undefined;
  if (
    response?.status === 400 &&
    response.data?.errorCode === "ASSOCIATION_INVALID_INPUT"
  ) {
    const message = response.data.message;
    return typeof message === "string" && message.trim()
      ? message
      : MEDIA_INVALID_INPUT_ERROR;
  }
  return MEDIA_SAVE_ERROR;
};

interface MediaCardProps {
  association: AssociationResponse;
  onSaved: () => void;
  /**
   * The page-level ASSOCIATION_NOT_ADMINISTERED handler (copy that outlives
   * the card, plus the re-read of `/mine`).
   */
  onNotAdministered?: () => void;
  /** This card's save is in flight (the page's cross-card lock). */
  onPendingChange?: (pending: boolean) => void;
  /** Picked files or removed stored rows not saved yet. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Another card's save is in flight: this card's save waits for it. */
  locked?: boolean;
}

const sameRows = (a: StoredMediaFile[], b: StoredMediaFile[]): boolean =>
  a.length === b.length && a.every((row, index) => row.id === b[index].id);

const storedLogo = (logo: StoredMediaFile | null): LogoSlot =>
  logo ? { kind: "stored", file: logo } : null;

/** The slot still holds exactly the stored logo it was seeded from (or both are empty). */
const sameLogo = (slot: LogoSlot, baseline: StoredMediaFile | null): boolean =>
  slot === null
    ? baseline === null
    : slot.kind === "stored" && slot.file.id === baseline?.id;

const MediaCard: React.FC<MediaCardProps> = ({
  association,
  onSaved,
  onNotAdministered,
  onPendingChange,
  onDirtyChange,
  locked = false,
}) => {
  const [kept, setKept] = useState<StoredMediaFile[]>(association.images);
  // The stored rows the editor was last seeded from.
  const [baseline, setBaseline] = useState<StoredMediaFile[]>(
    association.images,
  );
  const [pending, setPending] = useState<PendingMediaFile[]>([]);
  const [logo, setLogo] = useState<LogoSlot>(storedLogo(association.logo));
  // The stored logo the slot was last seeded from.
  const [baselineLogo, setBaselineLogo] = useState<StoredMediaFile | null>(
    association.logo,
  );
  const [logoError, setLogoError] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // A different association: everything in the editor belonged to the
  // previous one (its stored rows, the files picked for it), so all of it is
  // replaced. The first render is seeded by the state initialisers above.
  const shownId = useRef(association.id);
  useEffect(() => {
    if (shownId.current === association.id) {
      return;
    }
    shownId.current = association.id;
    setKept(association.images);
    setBaseline(association.images);
    setPending([]);
    setLogo(storedLogo(association.logo));
    setBaselineLogo(association.logo);
    setLogoError(null);
    setMediaError(null);
    setSaveError(null);
    setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [association.id]);

  const dirty =
    pending.length > 0 ||
    !sameRows(kept, baseline) ||
    !sameLogo(logo, baselineLogo);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    onPendingChange?.(saving);
  }, [saving, onPendingChange]);
  // An unmounted card holds no edits and no save the page must wait for.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);

  const changeKept = (next: StoredMediaFile[]) => {
    setNotice(null);
    setKept(next);
  };
  const changePending = (next: PendingMediaFile[]) => {
    setNotice(null);
    setPending(next);
  };
  const changeLogo = (next: LogoSlot) => {
    setNotice(null);
    setLogo(next);
  };

  const save = async () => {
    if (saving || locked) {
      return;
    }
    const savedFor = association.id;
    const { images, files } = buildAssociationImagesUpdate(
      logo,
      kept,
      pending,
    );
    const request: AssociationRequest = {
      name: association.name,
      description: association.description,
      address: association.address,
      city: association.city,
      postalCode: association.postalCode,
      phoneNumber: association.phoneNumber,
      latitude: association.latitude,
      longitude: association.longitude,
      categoryIds: association.categories.map((category) => category.id),
      active: null,
      workingHours: null,
      images,
      updateImages: true,
    };
    setSaving(true);
    setSaveError(null);
    setNotice(null);
    try {
      const saved = await updateAssociation(savedFor, request, files);
      // Rebase on what the server stored: this turns the uploaded files into
      // stored rows, so a second save does not upload them again. Skipped when
      // the user switched association while the request was in flight.
      if (shownId.current === savedFor) {
        setKept(saved.images);
        setBaseline(saved.images);
        setPending([]);
        setLogo(storedLogo(saved.logo));
        setBaselineLogo(saved.logo);
        setLogoError(null);
        setMediaError(null);
        setNotice(MEDIA_SAVED_NOTICE);
      }
      onSaved();
    } catch (error) {
      if (onNotAdministered && isAssociationNotAdministered(error)) {
        // Plan 1.3: the page shows the copy and re-reads `/mine`.
        onNotAdministered();
        return;
      }
      // The picked files and removals stay, so the user can retry as is.
      if (shownId.current === savedFor) {
        setSaveError(mapMediaSaveError(error));
      }
      if (shouldRefetchAssociationsAfterError(error)) {
        // The association is gone (a 404 after another administrator deleted
        // it) or, without a page handler, the pairing is: re-read `/mine`.
        onSaved();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className={styles.card}
      aria-labelledby="association-media-title"
      data-testid="media-card"
    >
      <h2 id="association-media-title" className={styles.cardTitle}>
        Mediji
      </h2>
      <LogoPicker
        name={association.name}
        logo={logo}
        disabled={saving}
        onChange={changeLogo}
        error={logoError}
        onError={setLogoError}
      />
      <MediaManager
        kept={kept}
        pending={pending}
        disabled={saving}
        onKeptChange={changeKept}
        onPendingChange={changePending}
        error={mediaError}
        onError={setMediaError}
      />
      {saveError && (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      )}
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      <Button buttonType="blue" disabled={saving || locked} onClick={save}>
        {saving ? "Čuvanje..." : "Sačuvaj medije"}
      </Button>
    </section>
  );
};

export default MediaCard;
